/**
 * Enhanced activity builder that handles:
 * 1. Bracket order entries (AI-driven)
 * 2. Manual market entries (user-initiated)
 * 3. Mixed scenarios (user starts, AI adds/exits)
 * 4. FIXED: Partial closes in double order scenarios (100->200->100->0)
 */

const buildActivities = function(activities) {
  if (!activities || activities.length === 0) {
    return [];
  }

  let tradeActivities = [];
  let sale_amt = 0;
  let buy_amt = 0;
  let short_qty = 0;
  let long_qty = 0;
  let open_qty = 0;
  let delta = 0;
  let pl = 0;
  let group = 0;
  let newTrade = true;
  
  // Track position direction to detect reversals
  let lastDirection = null; // 'long' or 'short'

  const calculateTaxes = (a) => {
    if (a.side === "sell" || a.side === "sell_short") {
      let qty = a.qty;
      let price = parseFloat(a.price);
      const FINRA_TAX = 0.000119;
      const SEC_FEES_LOT = 22.1;
      let SEC_cost_per_trade = ((price * qty) / 1000000) * SEC_FEES_LOT;
      let finra_cost = (qty * FINRA_TAX).toFixed(2);
      let total_fees = SEC_cost_per_trade + parseFloat(finra_cost);
      return parseFloat(total_fees).toFixed(2);
    } else {
      return 0.00;
    }
  };

  // Determine if this activity represents a position entry
  const isEntry = (activity, prevOpenQty) => {
    const currentDirection = activity.side === "buy" ? "long" : "short";
    
    // First trade in a group
    if (prevOpenQty === 0) {
      return true;
    }
    
    // Direction reversal (close previous, open opposite)
    if (lastDirection && currentDirection !== lastDirection) {
      return true;
    }
    
    // Adding to position (same direction, increasing qty)
    return false;
  };

  for (let i = 0; i < activities.length; i++) {
    const activity = activities[i];
    const prev_open_qty = open_qty;
    
    // Determine current direction
    const currentDirection = (activity.side === "buy") ? "long" : "short";
    
    // Check if we need to start a new group
    if (newTrade) {
      group += 1;
      newTrade = false;
      lastDirection = currentDirection;
      console.log(`✨ Starting new group ${group} - Direction: ${currentDirection}`);
    } else if (lastDirection && currentDirection !== lastDirection && open_qty !== 0) {
      // Direction changed while we have open position
      // CRITICAL FIX: Don't start new group if this activity will just close/reduce the position
      // Only start new group if this activity will REVERSE direction (cross zero)
      
      // Calculate what open_qty will be AFTER this activity is processed
      let nextOpenQty = open_qty;
      if (activity.side === "sell" || activity.side === "sell_short") {
        nextOpenQty = open_qty - parseInt(activity.qty);
      } else if (activity.side === "buy") {
        nextOpenQty = open_qty + parseInt(activity.qty);
      }
      
      // Only mark for new group if we're going to REVERSE direction (cross zero into opposite sign)
      // Examples:
      // ✓ open_qty = 100, SELL 100 → next = 0 (CLOSE, stay in group)
      // ✓ open_qty = 200, SELL 100 → next = 100 (REDUCE, stay in group)  
      // ✗ open_qty = 100, SELL 150 → next = -50 (REVERSAL! Start new group)
      // ✗ open_qty = -100, BUY 150 → next = 50 (REVERSAL! Start new group)
      if ((open_qty > 0 && nextOpenQty < 0) || (open_qty < 0 && nextOpenQty > 0)) {
        console.log(`🔄 Direction reversal detected: ${open_qty} → ${nextOpenQty}`);
        newTrade = true;
      }
      // else: This is just a closing/reducing activity in the same group
    }
    
    // Format date
    var dt = new Date(activity.transaction_time).toLocaleString("en-US");
    
    // Update quantities
    if (activity.side === "sell" || activity.side === "sell_short") {
      sale_amt = sale_amt + parseInt(activity.qty) * parseFloat(activity.price);
      short_qty = short_qty + parseInt(activity.qty);
    } else if (activity.side === "buy") {
      buy_amt = buy_amt + parseInt(activity.qty) * parseFloat(activity.price);
      long_qty = long_qty + parseInt(activity.qty);
    }

    // Calculate open quantity
    open_qty = long_qty - short_qty;
    
    console.log(`Activity ${i} (Group ${group}): ${activity.side} ${activity.qty} @ ${activity.price} | open_qty: ${open_qty}`);

    // Calculate P&L when position closes
    if (open_qty === 0 && prev_open_qty !== 0) {
      delta = sale_amt - buy_amt - pl;
      pl = sale_amt - buy_amt;
      newTrade = true;
      console.log(`✅ Group ${group} closed - P&L: $${delta.toFixed(2)}, Total P&L: $${pl.toFixed(2)}`);
      
      // Reset for next group
      lastDirection = null;
    } else {
      delta = 0;
    }

    const info = {
      group: group,
      id: activity.id,
      symbol: activity.symbol,
      a_id: activity.activity_id,
      date: dt,
      created_at: activity.created_at,
      created_at_gmt: activity.created_at_gmt,
      submitted_at: activity.submitted_at,
      submitted_at_gmt: activity.submitted_at_gmt,
      dateString: dt.toString(),
      price: activity.price,
      qty: activity.qty,
      side: activity.side,
      type: activity.type,
      limit: activity.limit,
      
      target: activity.target ?? 0,
      stop: activity.stop ?? 0,
      bracket: activity.bracket ?? null,
      
      pl: pl.toFixed(2),
      pl_delta: delta.toFixed(2),
      cum_qty: activity.cum_qty,
      open_qty: long_qty - short_qty,
      buy: activity.side === "buy" ? activity.price : 0,
      sell: activity.side === "sell" ? activity.price : 0,
      sell_short: activity.side === "sell_short" ? activity.price : 0,
      pressure_BOD: isNaN(activity.pressure_BOD)
        ? 0
        : activity.pressure_BOD.toFixed(3),
      pressure_window: isNaN(activity.pressure_window)
        ? 0
        : activity.pressure_window.toFixed(3),
      c_delta: isNaN(activity.c_delta) ? 0 : activity.c_delta.toFixed(3),
    };
    
    let tax = calculateTaxes(info);
    info.tax = tax;
    tradeActivities.push(info);
  }

  console.log(`🏁 Built ${tradeActivities.length} activities across ${group} groups`);
  return tradeActivities;
};

export default buildActivities;